import React, {useState, useEffect, useContext} from 'react';
import glamorous from 'glamorous';
import {WHITE, ASH, FOCUS_BLUE, PINTEREST_RED} from '../../style/colors';
import CheckmarkIcon from '../../../shared/library/icons/checkmark-icon.svg';
import GitIconDefault from '../../../shared/library/icons/git-icon.svg';
import FailedIcon from '../../../shared/library/icons/failed-v3.svg';
import {ALPHA} from '../../style/color-utils';
import SimpleButton from '../../../shared/library/buttons/base/simple';
import {usePolling} from '../../../shared/utils/hooks/polling';
import {repoAnalysisProgress} from '../../../../app/data/company-team/queries.js';
import CloseIcon from '../../../shared/library/icons/close.svg';
import * as glamor from 'glamor';
import {withPrivateMode} from '../../enhancers/private-mode-enchancer';
import {ApolloContext} from '../../enhancers/graphql-enhancer';
import gql from 'graphql-tag';
import {PropTypes} from 'mobx-react';

const Container = glamorous.div(({showMarginTop}) => ({
  width: '100%',
  border: `solid 1px ${ASH}`,
  borderRadius: 4,
  marginBottom: 22,
  marginTop: showMarginTop ? 50 : 0
}));

const Header = glamorous.div({
  display: 'flex',
  alignItems: 'center',
  height: 30,
  paddingLeft: 10,
  backgroundColor: FOCUS_BLUE,
  fontSize: 12,
  color: WHITE,
  position: 'relative'
});

const GitIcon = glamorous(GitIconDefault)({
  width: 16,
  height: 16,
  marginRight: 8
});

const ProgressBarContainer = glamorous.div({
  backgroundColor: WHITE,
  borderTop: 0,
  padding: 15
});

const ProgressContainer = glamorous.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: 11,
  fontWeight: 600
});

const Progress = glamorous.div({
  flex: '1 0 auto',
  height: 6,
  background: '#f2f2f2'
});

export const magic = glamor.css.keyframes({
  '0%': {backgroundPosition: `0 0`},
  '100%': {backgroundPosition: `100em 0`}
});

const ProgressDone = glamorous.div(({completed, error, progressDone, loading}) => ({
  width: `${progressDone}%`,
  transition: 'width 0.5s ease',
  animation: `7s ${magic} 0s linear infinite`,
  height: 6,
  background: error
    ? ALPHA(PINTEREST_RED, 0.2)
    : completed
    ? '#38cb6b'
    : `linear-gradient(to right, ${FOCUS_BLUE} 0%, ${
        loading ? ALPHA(FOCUS_BLUE, 0.1) : ALPHA(FOCUS_BLUE, 0.4)
      } 50%, ${FOCUS_BLUE} 100%)`
}));

const StacksImportedContainer = glamorous.div(({error}) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 900,
  minWidth: 165,
  flex: '0 0 auto',
  marginRight: 5,
  color: error ? PINTEREST_RED : '#777777',
  '& > span': {
    fontWeight: 400,
    marginLeft: 4
  }
}));

const StacksImported = glamorous.div({
  height: 19,
  borderRadius: 10,
  background: FOCUS_BLUE,
  fontSize: 10,
  fontWeight: 'bold',
  lineHeight: 1.9,
  letterSpacing: 0.22,
  color: WHITE,
  display: 'flex',
  justifyContent: 'center',
  marginRight: 8,
  padding: '0 8px'
});

const Checkmark = glamorous(CheckmarkIcon)({
  width: 19,
  height: 19,
  marginRight: 8,
  '& > g > circle': {
    fill: '#38cb6b'
  }
});

const Failed = glamorous(FailedIcon)({
  width: 19,
  height: 19,
  marginRight: 8
});

const Notice = glamorous.div(({error = false}) => ({
  borderRadius: 4,
  background: ALPHA(error ? PINTEREST_RED : FOCUS_BLUE, 0.1),
  padding: '13px 23px',
  marginTop: 20,
  fontSize: 11,
  lineHeight: 1.45,
  color: error ? PINTEREST_RED : FOCUS_BLUE,
  position: 'relative',
  '& > a': {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.92,
    color: FOCUS_BLUE,
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  '& > p': {
    marginTop: error ? 0 : 9,
    marginBottom: error ? 0 : 9
  }
}));

const Button = glamorous(SimpleButton)({
  position: 'absolute',
  right: 23,
  bottom: 13,
  minWidth: 80,
  height: 24,
  fontSize: 10,
  lineHeight: 1.4,
  color: WHITE
});

const Close = glamorous(CloseIcon)({
  position: 'absolute',
  right: 10
});

const ProgressBar = ({query = repoAnalysisProgress, privateMode, showMarginTop = false}) => {
  const [startedImporting, setStartedImporting] = useState(false);
  const [importFailed, setImportFailed] = useState(false);
  const [importCompleted, setImportCompleted] = useState(false);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(null);
  const [progress, setProgress] = useState(0);

  const resp = usePolling({
    query: query,
    variables: {},
    pollingTime: 500,
    checkIfNeedToRefetch: data =>
      data.currentPrivateCompany && data.currentPrivateCompany.repoScanProgress.should_poll
  });

  const data = resp && resp.data;

  const progess =
    data && data.currentPrivateCompany && data.currentPrivateCompany.repoScanProgress.progress;

  const {success = 0, pending = 0, failure = 0} = progess ? progess : {};

  const total = success + pending + failure;

  const shouldPollFromData = data
    ? data.currentPrivateCompany && data.currentPrivateCompany.repoScanProgress.should_poll
    : null;

  const repoStacksCount =
    data && data.currentPrivateCompany && data.currentPrivateCompany.repoStacksCount;

  useEffect(() => {
    if (shouldPoll === null && shouldPollFromData !== null) {
      if (shouldPollFromData) setShowProgressBar(true);
    }
    setShouldPoll(shouldPollFromData);
  }, [shouldPollFromData]);

  useEffect(() => {
    if (!startedImporting) setStartedImporting(success > 0 || pending > 0 || failure > 0);

    if (!shouldPollFromData && success > 0 && pending === 0) setImportCompleted(true);

    if (!shouldPollFromData && (success === 0 && pending === 0 && failure > 0))
      setImportFailed(true);
  }, [data]);

  useEffect(() => {
    if (success > 0) {
      setProgress(Math.ceil((success * 100) / total));
    }
  }, [success, progress]);

  return (
    showProgressBar && (
      <Container showMarginTop={showMarginTop}>
        <Header>
          <GitIcon />
          Git Repository Import
          {importFailed && <Close onClick={() => setShowProgressBar(false)} />}
        </Header>
        <ProgressBarContainer>
          {!startedImporting && (
            <ProgressContainer>
              <StacksImportedContainer>Receiving Data…</StacksImportedContainer>
              <Progress>
                <ProgressDone progressDone={100} loading={true} />
              </Progress>
            </ProgressContainer>
          )}

          {startedImporting && !importCompleted && (
            <ProgressContainer>
              <StacksImportedContainer>
                <StacksImported>
                  <span>
                    {success} / {total}
                  </span>
                </StacksImported>
                Repos imported
              </StacksImportedContainer>
              <Progress>
                <ProgressDone progressDone={progress} />
              </Progress>
            </ProgressContainer>
          )}

          {importCompleted && !importFailed && (
            <CompletedStage
              repoStacksCount={repoStacksCount}
              importCompleted={importCompleted}
              importFailed={importFailed}
              privateMode={privateMode}
              setShowProgressBar={setShowProgressBar}
            />
          )}
          {importCompleted && importFailed && (
            <>
              <ProgressContainer>
                <StacksImportedContainer error={importFailed}>
                  <Failed />
                  Import failed
                </StacksImportedContainer>
                <Progress>
                  <ProgressDone
                    completed={importCompleted}
                    error={importFailed}
                    progressDone={progress}
                  />
                </Progress>
              </ProgressContainer>
              <Notice error={importFailed}>
                <p>
                  We apologize for the inconvenience, an unexpected error occurred.
                  <br />
                  Please contact us and we’ll investigate and get back to you.
                </p>
                <Button onClick={() => window.open('mailto:private@stackshare.io')}>
                  Contact Us
                </Button>
              </Notice>
            </>
          )}
        </ProgressBarContainer>
      </Container>
    )
  );
};

const fetchStages = gql`
  query stacksList {
    currentPrivateCompany {
      id
      stacksList {
        count
        edges {
          node {
            id
            slug
            repoStack
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

const CompletedStage = ({
  repoStacksCount,
  importCompleted,
  importFailed,
  privateMode,
  setShowProgressBar
}) => {
  const client = useContext(ApolloContext);
  const [loading, setLoading] = useState(true);
  const [totalStackCount, setTotalStackCount] = useState(repoStacksCount);

  useEffect(() => {
    setTimeout(() => {
      client.query({query: fetchStages, fetchPolicy: 'network-only'}).then(resp => {
        if (!resp.data.loading) {
          const stackLength = resp.data.currentPrivateCompany.stacksList.edges.filter(
            item => item.node.repoStack
          ).length;
          setTotalStackCount(stackLength);
          setLoading(false);
        }
      });
    }, 3000);
  }, []);

  return (
    <>
      <ProgressContainer>
        {loading ? (
          <>
            <StacksImportedContainer>Processing Data…</StacksImportedContainer>
            <Progress>
              <ProgressDone progressDone={100} loading={true} />
            </Progress>
          </>
        ) : (
          <>
            <StacksImportedContainer>
              <Checkmark />
              {totalStackCount}
              <span>Stacks created!</span>
            </StacksImportedContainer>
            <Progress>
              <ProgressDone completed={importCompleted} error={importFailed} progressDone={100} />
            </Progress>
          </>
        )}
      </ProgressContainer>
      {!loading && (
        <Notice error={false}>
          <a href={`/companies/${privateMode.slug}#tech-stacks`}>View your created stacks </a>
          <p>
            of imported stacks will not always be equal to the number of repositories scanned.
            <br />
            This happens if no tools are detected in a repo or if the repo is archived.
          </p>
          <Button onClick={() => setShowProgressBar(false)}>Close</Button>
        </Notice>
      )}
    </>
  );
};

ProgressBar.propTypes = {
  privateMode: PropTypes.bool,
  showMarginTop: PropTypes.number,
  query: PropTypes.object
};

CompletedStage.propTypes = {
  repoStacksCount: PropTypes.number,
  importCompleted: PropTypes.bool,
  importFailed: PropTypes.bool,
  privateMode: PropTypes.bool,
  setShowProgressBar: PropTypes.func
};

export default withPrivateMode(ProgressBar);
